import { customersApiSlice } from '../../../../../slices/customersApiSlice';
import { statisticsApiSlice } from '../../../../../slices/statisticsApiSlice';
import { usersApiSlice } from '../../../../../slices/usersApiSlice';
import { buildStatisticsQueryParams } from '../../../filters/lib/statisticsFilterParams';
import {
	buildPersistedChartFilters,
	isPipelineBuiltinChartKind,
	resolvePersistedChartIncludeOrders,
	resolvePersistedChartKind,
	PERSISTED_CHART_KIND,
} from './statisticsPersistedCharts';
import { buildBuiltinLiveFreezeBundle, chartUsesPageFilters } from './dashboardChartLifecycle';
import { resolveDashboardChartBundleEntry } from './buildDashboardChartBundle';

const unwrapCachedQuery = async (dispatch, endpoint, arg, fallback) => {
	try {
		return await dispatch(endpoint.initiate(arg, { subscribe: false })).unwrap();
	} catch {
		return fallback;
	}
};

/**
 * 单图刷新快照：走 batch mutation，不订阅页面级 getOrderPrimaryPhaseStats，避免同参缓存联动其它图。
 */
export const refreshFrozenChartBundle = async ({
	dispatch,
	snapshot,
	overdueData = null,
	pageStatsState = null,
	isManager = false,
}) => {
	if (!snapshot) return null;

	const chartKind = resolvePersistedChartKind(snapshot);
	const liveSnapshot = { ...snapshot, refreshMode: 'live', dataSnapshot: undefined };

	if (chartKind === PERSISTED_CHART_KIND.OVERDUE) {
		if (chartUsesPageFilters(snapshot) && pageStatsState) {
			return buildBuiltinLiveFreezeBundle({ snapshot, statsState: pageStatsState, overdueData });
		}
		return resolveDashboardChartBundleEntry({
			snapshot: liveSnapshot,
			overdueData,
			forceLive: true,
		});
	}

	if (isPipelineBuiltinChartKind(chartKind) && chartUsesPageFilters(snapshot) && pageStatsState) {
		return buildBuiltinLiveFreezeBundle({ snapshot, statsState: pageStatsState, overdueData });
	}

	const filters = buildPersistedChartFilters(snapshot);
	if (!filters && !isPipelineBuiltinChartKind(chartKind)) return null;

	const query = buildStatisticsQueryParams(filters, {
		includeOrders: resolvePersistedChartIncludeOrders(snapshot),
		compositionCompareSeries: Boolean(snapshot?.chartConfig?.compareSeries),
	});

	const [batchData, fetchedSaleUsers, fetchedAssistantUsers, fetchedCustomers] = await Promise.all([
		dispatch(
			statisticsApiSlice.endpoints.postOrderPrimaryPhaseStatsBatch.initiate([{ key: snapshot.id, query }]),
		).unwrap(),
		unwrapCachedQuery(dispatch, usersApiSlice.endpoints.getSaleUsers, { scope: 'order' }, []),
		unwrapCachedQuery(dispatch, usersApiSlice.endpoints.getAssistantUsers, { scope: 'order' }, []),
		isManager ? unwrapCachedQuery(dispatch, customersApiSlice.endpoints.getCustomers, undefined, []) : Promise.resolve([]),
	]);

	return resolveDashboardChartBundleEntry({
		snapshot: liveSnapshot,
		primaryPhaseStats: batchData?.results?.[snapshot.id] ?? null,
		overdueData,
		fetchedSaleUsers,
		fetchedAssistantUsers,
		fetchedCustomers,
		forceLive: true,
	});
};
